import { t } from "i18next";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "@material-tailwind/react";
import { GenericListLayout } from "../../../components/generic-list-view/GenericListLayout";
import { Table } from "../../../components/table/Table";
import { Pagination } from "../../../components/pagination/Pagination";
import { api } from "../../../services/api/api";
import toast from "react-hot-toast";
import { useTablePageSize } from "../../../hooks/useTablePageSize";

/**
* InterventionsTab: display machine interventions
* @param data - machine data
*/
const InterventionsTab = ({ data }: { data: EasyTech.Machine }) => {
  const navigate = useNavigate();
  const [allInterventions, setAllInterventions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pageState, setPageState] = useState<number>(0);

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const fetchInterventions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`users/workorders/linked/${data.id}`) as any;
      setAllInterventions(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des interventions:", error);
      toast.error(t("An error occurred while fetching interventions"));
    } finally {
      setIsLoading(false);
    }
  }, [data.id]);

  useEffect(() => {
    fetchInterventions();
  }, [fetchInterventions]);

  /**
  * La tranche affichée et le nombre de pages se déduisent de la liste complète.
  *
  * Ils étaient auparavant deux states alimentés par une fonction qui lisait la taille de page dans
  * sa fermeture, avec `pageState` dans les dépendances du fetch : changer de page relançait la
  * requête, et rendre la taille réactive aurait créé trois chemins de fermeture périmée d'un coup.
  * Dériver supprime les trois, et fait que la table se vide bien quand la liste devient vide — la
  * garde `length > 0` de l'ancien effet l'en empêchait.
  */
  const interventions = useMemo(
    () => allInterventions.slice(pageState * pageSize, (pageState + 1) * pageSize),
    [allInterventions, pageState, pageSize]
  );

  const pageNumberState = useMemo(
    () => Math.ceil(allInterventions.length / pageSize),
    [allInterventions, pageSize]
  );

  const handleInterventionClick = (intervention: any) => {
    navigate(`/workorders/${intervention.id}`);
  };

  const tableHead = [
    { label: t("machine:ref"), orderKey: "ref" },
    { label: t("machine:start_date:label"), orderKey: "schedule.start" },
    { label: t("machine:start_time:label"), orderKey: "schedule.start" },
    { label: t("machine:end_date:label"), orderKey: "schedule.end" },
    { label: t("machine:end_time:label"), orderKey: "schedule.end" },
    { label: t("machine:contact:label"), orderKey: null },
    { label: t("machine:technician:label"), orderKey: "technician.lastName" },
    { label: t("machine:notes:label"), orderKey: "notes" }
  ];

  // Helper function to format date
  const formatDate = (timestamp: number) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Helper function to format time
  const formatTime = (timestamp: number) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const content = (
    <div className="overflow-x-auto mt-4">
      <Table
        tableHead={tableHead}
        emptyTable={interventions.length === 0 && t("workorder:noData")}
        enableRowLinks={true}
      >
        {interventions.map((intervention, index) => {
          const isLast = index === interventions.length - 1;
          const classes = isLast ? "p-2" : "p-2 border-b border-blue-gray-50";
          const interventionUrl = `/workorders/${intervention.id}`;

          return (
            <tr 
              key={intervention.id || index}
              onClick={() => handleInterventionClick(intervention)}
              className="cursor-pointer hover:bg-gray-50"
              data-href={interventionUrl}
            >
              <td className={classes}>
                <Typography variant="small" color="blue-gray">
                  {intervention.ref || "-"}
                </Typography>
              </td>
              <td className={classes}>
                <Typography variant="small" color="blue-gray">
                  {intervention.schedule?.start ? formatDate(intervention.schedule.start) : "-"}
                </Typography>
              </td>
              <td className={classes}>
                <Typography variant="small" color="blue-gray">
                  {intervention.schedule?.start ? formatTime(intervention.schedule.start) : "-"}
                </Typography>
              </td>
              <td className={classes}>
                <Typography variant="small" color="blue-gray">
                  {intervention.schedule?.end ? formatDate(intervention.schedule.end) : "-"}
                </Typography>
              </td>
              <td className={classes}>
                <Typography variant="small" color="blue-gray">
                  {intervention.schedule?.end ? formatTime(intervention.schedule.end) : "-"}
                </Typography>
              </td>
              <td className={classes}>
                <Typography variant="small" color="blue-gray">
                  {intervention.contacts && intervention.contacts.length > 0 
                    ? `${intervention.contacts[0].contact.firstName} ${intervention.contacts[0].contact.lastName}`
                    : "-"}
                </Typography>
              </td>
              <td className={classes}>
                <Typography variant="small" color="blue-gray">
                  {intervention.technician 
                    ? `${intervention.technician.firstName} ${intervention.technician.lastName}`
                    : "-"}
                </Typography>
              </td>
              <td className={classes}>
                <Typography variant="small" color="blue-gray">
                  {intervention.notes || "-"}
                </Typography>
              </td>
            </tr>
          );
        })}
      </Table>
    </div>
  );

  const paginationComponent = allInterventions && allInterventions.length > pageSize ? (
    <Pagination
      actualPage={pageState}
      pageNumber={pageNumberState}
      changePage={setPageState}
    />
  ) : null;

  return (
    <GenericListLayout
      entityName={t("machine:tabs:interventions")}
      isLoading={isLoading}
      content={content}
      pagination={paginationComponent}
      showBreadcrumb={false}
    />
  );
};

export default InterventionsTab; 